import { useContext, useEffect, useRef } from "react";
import { SocketContext } from "../../../SocketConnection";

// A burst of driver activity (arrive, complete, depart on one stop) arrives
// as several socket events; they are collapsed into a single refetch.
const REFETCH_DELAY_MS = 500;

// The socket events are what keep the page current; this is only a safety
// net for an event that never arrived.
const FALLBACK_REFETCH_INTERVAL_MS = 60_000;

const LIVE_VIEW_EVENTS = [
  // Any change the driver can see on the route: stops added, removed or
  // reordered, arrivals, departures, completions. Sent to the route's room.
  "driver-route.version-changed",
  // A driver started or ended a trip. Sent to the org's room (joined on
  // connect) and to the route's room.
  "live-view.sessions-changed",
];

// live-view-route: refetch the live view when the server says something on one
// of the shown routes changed, instead of polling every few seconds.
export const useLiveViewUpdates = (input: {
  enabled: boolean;
  routeIds: string[];
  onUpdate: () => void;
}) => {
  const { enabled, routeIds, onUpdate } = input;
  const socket = useContext(SocketContext);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  // A stable key, so the rooms are only re-joined when the set of routes
  // changes, not on every render.
  const routeIdsKey = [...new Set(routeIds)].sort().join(",");

  useEffect(() => {
    if (!enabled || !socket) {
      return;
    }
    const ids = routeIdsKey ? routeIdsKey.split(",") : [];
    const joinRooms = () => {
      ids.forEach((routeId) =>
        socket.emit("join-live-view-room", { routeId })
      );
    };
    // Rooms do not survive a reconnect, and events may have been missed
    // while disconnected.
    const onReconnect = () => {
      joinRooms();
      onUpdateRef.current();
    };
    joinRooms();
    socket.on("connect", onReconnect);
    return () => {
      socket.off("connect", onReconnect);
      ids.forEach((routeId) =>
        socket.emit("leave-live-view-room", { routeId })
      );
    };
  }, [enabled, socket, routeIdsKey]);

  useEffect(() => {
    if (!enabled || !socket) {
      return;
    }
    let pendingRefetch: ReturnType<typeof setTimeout> | null = null;
    const scheduleRefetch = () => {
      if (pendingRefetch) {
        return;
      }
      pendingRefetch = setTimeout(() => {
        pendingRefetch = null;
        onUpdateRef.current();
      }, REFETCH_DELAY_MS);
    };
    LIVE_VIEW_EVENTS.forEach((event) => socket.on(event, scheduleRefetch));
    return () => {
      LIVE_VIEW_EVENTS.forEach((event) => socket.off(event, scheduleRefetch));
      if (pendingRefetch) {
        clearTimeout(pendingRefetch);
      }
    };
  }, [enabled, socket]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const interval = setInterval(
      () => onUpdateRef.current(),
      FALLBACK_REFETCH_INTERVAL_MS
    );
    return () => clearInterval(interval);
  }, [enabled]);
};
